import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Sparkles, Check } from "lucide-react";
import {
  getStyleGallery,
  generateStylePreview,
  listChannelCreations,
  putChannelSettings,
} from "@/lib/api";
import PresetRefManager from "../components/PresetRefManager";

export default function StyleGallery() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-preset regen state so clicking Refresh on one card doesn't
  // block the others.
  const [regen, setRegen] = useState({});
  // Per-preset "Set as Channel Default" state — tracks which channel
  // handle is selected in the picker and whether an apply is in
  // flight. Kept flat and keyed by preset.key so cards stay
  // independent from each other.
  const [pickedHandle, setPickedHandle] = useState({});
  const [applying, setApplying] = useState({});
  const [channels, setChannels] = useState([]);

  const load = useCallback(async () => {
    try {
      const [gallery, list] = await Promise.all([
        getStyleGallery(),
        listChannelCreations().catch(() => ({ items: [] })),
      ]);
      setPresets(gallery?.presets || []);
      // De-dupe by handle_key so a user with two kit-jobs on the same
      // @handle only shows once in the picker.
      const seen = new Set();
      const dedup = [];
      for (const c of list?.items || []) {
        const handle = (c.handle || "").replace(/^@/, "").toLowerCase();
        if (!handle || seen.has(handle)) continue;
        seen.add(handle);
        dedup.push({
          handle,
          name: c.name || `@${handle}`,
        });
      }
      setChannels(dedup);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load style gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRegen = async (key) => {
    if (regen[key]) return;
    setRegen((r) => ({ ...r, [key]: true }));
    try {
      const d = await generateStylePreview(key, { force: true });
      // Swap in the fresh URL for this card only.
      setPresets((rows) =>
        rows.map((p) =>
          p.key === key ? { ...p, sample_url: d?.url || p.sample_url } : p,
        ),
      );
      toast.success("Sample refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRegen((r) => ({ ...r, [key]: false }));
    }
  };

  const onApplyToChannel = async (presetKey) => {
    const handle = pickedHandle[presetKey];
    if (!handle) {
      toast.error("Pick a channel first");
      return;
    }
    if (applying[presetKey]) return;
    setApplying((a) => ({ ...a, [presetKey]: true }));
    try {
      // 1. Persist the choice as the channel's default style preset.
      await putChannelSettings(handle, { style_preset: presetKey });
      // 2. Kick off a channel-themed sample so this card now
      //    previews the style ON that channel's topic.
      const d = await generateStylePreview(presetKey, {
        channelHandle: handle,
        force: true,
      });
      setPresets((rows) =>
        rows.map((p) =>
          p.key === presetKey ? { ...p, sample_url: d?.url || p.sample_url } : p,
        ),
      );
      const label =
        channels.find((c) => c.handle === handle)?.name || `@${handle}`;
      toast.success(`Applied to ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply style");
    } finally {
      setApplying((a) => ({ ...a, [presetKey]: false }));
    }
  };

  const withSample = presets.filter((p) => p.sample_url).length;

  return (
    <div
      data-testid="style-gallery-page"
      className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto"
    >
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        {"// STYLE GALLERY"}
      </div>
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-none mb-3">
            Every look, side-by-side.
          </h1>
          <p className="text-zinc-300 text-sm max-w-2xl leading-relaxed">
            Browse every visual style before committing to one for a
            channel. Each card is a real sample of that style — pick
            a channel to apply the look as its default and re-roll a
            themed sample in one click.
          </p>
          <p className="text-zinc-500 text-xs mt-2 font-mono uppercase tracking-[0.25em]">
            · {presets.length} styles · {withSample} with samples ready
            {channels.length > 0 && (
              <> · {channels.length} channels available</>
            )}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading gallery<span className="blink">_</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {presets.map((p) => (
            <StyleCard
              key={p.key}
              preset={p}
              regenerating={!!regen[p.key]}
              onRegen={() => onRegen(p.key)}
              channels={channels}
              pickedHandle={pickedHandle[p.key] || ""}
              onPickHandle={(h) =>
                setPickedHandle((s) => ({ ...s, [p.key]: h }))
              }
              applying={!!applying[p.key]}
              onApply={() => onApplyToChannel(p.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StyleCard({
  preset, regenerating, onRegen,
  channels, pickedHandle, onPickHandle,
  applying, onApply,
}) {
  const [imgOk, setImgOk] = useState(true);
  const busy = regenerating || applying;
  const busyLabel = useMemo(() => {
    if (applying) return "applying…";
    if (regenerating) return "rendering…";
    return "";
  }, [applying, regenerating]);
  return (
    <div
      data-testid={`style-card-${preset.key}`}
      className="border border-zinc-900 hover:border-yellow-400/40 rounded-sm overflow-hidden bg-zinc-950/60 transition-colors flex flex-col"
    >
      <div className="relative aspect-square bg-zinc-900 overflow-hidden">
        {preset.sample_url && imgOk ? (
          <img
            src={preset.sample_url}
            alt=""
            loading="lazy"
            onError={() => setImgOk(false)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-zinc-500">
            {busy
              ? <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
              : <Sparkles className="w-6 h-6 text-zinc-700" />}
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]">
              {busy ? busyLabel : "no sample yet"}
            </div>
          </div>
        )}
        {busy && preset.sample_url && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
              {busyLabel}
            </div>
          </div>
        )}
      </div>
      <div className="p-4 space-y-2 flex-1 flex flex-col">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400">
          {preset.key}
        </div>
        <h3 className="text-base font-black text-white leading-tight">
          {preset.label}
        </h3>
        <p className="text-[12px] text-zinc-400 leading-relaxed flex-1">
          {preset.description}
        </p>

        {/* Per-user reference pack — upload thumbnails to lock this
            preset to the user's exact brand aesthetic. */}
        <PresetRefManager presetKey={preset.key} />

        {/* Apply-to-channel controls. Only rendered when the user has
            at least one channel — otherwise there's nothing to apply
            to and we'd just be surfacing a dead dropdown. */}
        {channels.length > 0 && (
          <div className="pt-3 border-t border-zinc-900 space-y-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500">
              set as channel default
            </div>
            <select
              data-testid={`style-card-${preset.key}-channel-select`}
              value={pickedHandle}
              onChange={(e) => onPickHandle(e.target.value)}
              disabled={busy}
              className="w-full bg-black border border-zinc-800 hover:border-zinc-700 focus:border-yellow-400 outline-none text-white text-xs px-2 py-1.5 rounded-sm font-mono disabled:opacity-40"
            >
              <option value="">— pick a channel —</option>
              {channels.map((c) => (
                <option key={c.handle} value={c.handle}>
                  {c.name} (@{c.handle})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onApply}
              disabled={busy || !pickedHandle}
              data-testid={`style-card-${preset.key}-apply`}
              className="w-full inline-flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-yellow-400/40 hover:border-yellow-400 hover:bg-yellow-400 hover:text-black text-yellow-400 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {applying
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Check className="w-3 h-3" />}
              {applying ? "applying…" : "set as channel default"}
            </button>
          </div>
        )}

        <div className="pt-3">
          <button
            type="button"
            onClick={onRegen}
            disabled={busy}
            data-testid={`style-card-${preset.key}-refresh`}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-zinc-800 hover:border-yellow-400 hover:text-yellow-400 text-zinc-400 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-wait"
          >
            {regenerating
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RefreshCcw className="w-3 h-3" />}
            {regenerating ? "rendering…" : "refresh sample"}
          </button>
        </div>
      </div>
    </div>
  );
}
